import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'keenez.theme';

/**
 * Persisted theme — light / dark / system. Writes / removes the
 * `.dark` class on `<html>` so every Tailwind dark: variant picks it
 * up. Listens for the OS preference change so `system` reflects it
 * live.
 */
export function useTheme(): { theme: Theme; effective: 'light' | 'dark'; setTheme: (t: Theme) => void } {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    return (window.localStorage.getItem(STORAGE_KEY) as Theme | null) || 'system';
  });
  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const effective: 'light' | 'dark' = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.toggle('dark', effective === 'dark');
  }, [effective]);

  const update = (next: Theme) => {
    setTheme(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* quota */ }
  };

  return { theme, effective, setTheme: update };
}
